import { FC, useCallback } from 'react';

import { useContextRouting } from '@core/context';
import { EActionAttribute } from '@core/type';

import { Box } from '../layout/Box';
import { PaginationEllipsis } from './PaginationEllipsis';
import { PaginationLink } from './PaginationLink';
import { PaginationLinkArrow } from './PaginationLinkArrow';
import { EPaginationStep, PaginationLinkArrowProps, PaginationProps } from './interface-pagination';

export const renderPageNumbers = (page: number, totalPages: number): number[] => {
  const pageNumbers = [];

  for (let i = 1; i <= totalPages; i++) {
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
    if (i === 1 || i >= totalPages - 1 || (i >= page - 3 && i <= page + 2)) {
      pageNumbers.push(i);
    }
  }

  return pageNumbers;
};

export const Pagination: FC<PaginationProps> = ({ page, perPage, itemsCount, onPageChange }) => {
  const { keyRoute, query } = useContextRouting();
  const totalPages = Math.ceil(itemsCount / perPage);

  const isFirstPage = page === 1;
  const isLastPage = page >= totalPages;

  const pageNumbers = renderPageNumbers(page, totalPages);

  const handleClick = useCallback(
    (newPage: number) => {
      if (newPage !== page && onPageChange) {
        onPageChange(newPage);
      }
    },
    [onPageChange, page],
  );

  const LinkArrow = useCallback(
    ({ page, ...restProps }: Partial<PaginationLinkArrowProps>) => {
      const queryPage = { ...query, page: page.toString() };
      if (page === 1) {
        delete queryPage.page;
      }
      return (
        <PaginationLinkArrow
          keyRoute={keyRoute}
          onClick={() => handleClick(page)}
          query={queryPage}
          {...restProps}
        />
      );
    },
    [query, handleClick, keyRoute],
  );

  // eslint-disable-next-line @typescript-eslint/no-magic-numbers
  if (totalPages < 2) {
    return null;
  }

  return (
    <Box
      as={EActionAttribute.UL}
      display={'flex'}
      justifyContent={'center'}
      alignItems={'center'}
      flexWrap={'wrap'}
      gap={'12px 24px'}
      role="navigation"
    >
      {!isFirstPage && (
        <>
          <LinkArrow isDouble isLeft page={1} label={EPaginationStep.FIRST} />
          <LinkArrow isLeft page={page - 1} label={EPaginationStep.PREV} />
        </>
      )}
      {pageNumbers.map((pageNumber, index) => {
        const isCurrentPage = pageNumber === page;
        const isFirstPage = index === 0;
        const isLastPage = index === pageNumbers.length - 1;
        const isPageNumberMatches = pageNumbers[index - 1] === pageNumber - 1;

        const queries = { ...query, page: pageNumber.toString() };
        if (pageNumber === 1) {
          delete queries.page;
        }

        return !(isFirstPage || isLastPage || isPageNumberMatches) ? (
          <PaginationEllipsis key={pageNumber} />
        ) : (
          <PaginationLink
            key={pageNumber}
            keyRoute={keyRoute}
            onClick={() => handleClick(pageNumber)}
            page={pageNumber}
            query={queries}
            isActive={isCurrentPage}
          >
            {pageNumber}
          </PaginationLink>
        );
      })}
      {!isLastPage && (
        <>
          <LinkArrow page={page + 1} label={EPaginationStep.NEXT} />
          <LinkArrow isDouble page={totalPages} label={EPaginationStep.LAST} />
        </>
      )}
    </Box>
  );
};
